/**
 * FREAK-ISH — Free Shipping Progress Bar (Live Update)
 * =====================================================
 * Listens to Shopify cart change events and updates the shipping bar
 * in real time without a page reload.
 *
 * Works with Horizon's cart drawer — subscribes to:
 *  - Shopify's native `cart:updated` custom event (fired by Horizon)
 *  - document event `cart-update` (fired by some Horizon versions)
 *  - Fetch intercept on /cart/change.js and /cart/add.js responses
 *
 * THRESHOLD: $75.00 (7500 cents). Change FREE_SHIPPING_THRESHOLD to adjust.
 */

(function () {
  'use strict';

  const FREE_SHIPPING_THRESHOLD = 7500; // cents — $75.00

  const bar = document.getElementById('freakish-shipping-bar');
  if (!bar) return;

  function formatMoney(cents) {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(dollars);
  }

  function update(cartTotalCents) {
    const remaining = FREE_SHIPPING_THRESHOLD - cartTotalCents;
    const pct = Math.min(100, Math.round((cartTotalCents / FREE_SHIPPING_THRESHOLD) * 100));

    const textEl = bar.querySelector('.freakish-shipping-bar__text');
    const fillEl = document.getElementById('freakish-shipping-bar__fill');
    const trackEl = bar.querySelector('.freakish-shipping-bar__track');

    if (!textEl || !fillEl) return;

    // Update fill width
    fillEl.style.width = `${pct}%`;

    // Update aria values
    if (trackEl) {
      trackEl.setAttribute('aria-valuenow', pct);
    }

    if (remaining > 0) {
      textEl.className = 'freakish-shipping-bar__text';
      textEl.innerHTML = `You're <strong>${formatMoney(remaining)}</strong> away from free shipping!`;
      fillEl.classList.remove('freakish-shipping-bar__fill--complete');
    } else {
      textEl.className = 'freakish-shipping-bar__text freakish-shipping-bar__text--unlocked';
      textEl.textContent = '🎉 You\'ve unlocked free shipping!';
      fillEl.classList.add('freakish-shipping-bar__fill--complete');
    }

    // Update data attribute
    bar.dataset.cartTotal = cartTotalCents;
  }

  // ── Horizon native cart events ───────────────────────────────────────────

  // Horizon dispatches 'cart:updated' on the document with cart data
  document.addEventListener('cart:updated', (e) => {
    const total = e?.detail?.cart?.total_price ?? e?.detail?.total_price;
    if (typeof total === 'number') update(total);
  });

  // Some Horizon versions use this event name
  document.addEventListener('cart-update', (e) => {
    const total = e?.detail?.total_price ?? e?.detail?.cart?.total_price;
    if (typeof total === 'number') update(total);
  });

  // ── Fetch intercept for /cart/*.js endpoints ─────────────────────────────
  // Catches cart mutations that don't fire a custom event

  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url ?? '';

    const isCartMutation =
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update') ||
      url.includes('/cart/clear');

    if (isCartMutation) {
      // Clone response so original stream is untouched
      const clone = response.clone();
      clone.json().then((data) => {
        // /cart/change.js and /cart/update.js return the full cart object
        const total = data?.total_price ?? data?.cart?.total_price;
        if (typeof total === 'number') update(total);
      }).catch(() => {});
    }

    return response;
  };

  // ── Init with server-rendered total ─────────────────────────────────────
  const initialTotal = parseInt(bar.dataset.cartTotal || '0', 10);
  update(initialTotal);
})();
